import * as React from "react"
import { Box } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { imageModalData } from "@signals"

const SIZE = 80

/**
 * Thumbnail of a brand row's sample product, for the two Puig brand tabs.
 *
 * Deliberately NOT built on `useProxiedImage`: that is a hook, so using it would force a
 * component-with-hooks per row, and one component per row is exactly what took this screen
 * down with "Should have a queue" (a MUI Tooltip per row, 1.000 rows). The URL work it does
 * is five lines and it is done once for the whole page in the panels — see `withThumbnails`
 * below. This component holds no hooks and no state.
 *
 * A click opens the app's global image modal (`AppImageModal`, mounted once in the
 * PageWrapper) through the `imageModalData` signal. `fullSRC` and not `src`: `src` sends the
 * URL to the backend's /sku/image-resource proxy, which is exactly what this screen avoids —
 * see the paragraph below. Writing a signal on click adds no subscription, so this component
 * still holds no hooks and no state. What opens is the retailer's own thumbnail, often already
 * cropped by their CDN (goldenscent serves `…/cache/2/thumbnail/75x75/…`), so it is a small
 * picture scaled up — still easier to read than the 80px cell, which is all this is for.
 *
 * The retailer CDNs are hit directly. A failure hides the image and leaves the placeholder
 * icon behind it showing, so the cell reads as "no picture" instead of looking like the app
 * dropped it: QA reported whole sites without images (bloomingdales.sa, unscraped since
 * 2024-10-23, whose CDN now answers 404 on every stored URL) and an empty cell gave no clue
 * why. The tooltip is set on the wrapper from `onError` rather than at render, so a picture
 * that loads fine never claims to be missing. `image` and not `image-slash`: that is the
 * icon name this codebase already uses 17 times, so it is certainly in the pack.
 *
 * No fallback to the backend's /sku/image-resource proxy, which the Matchers do use
 * (`ProductOption.jsx`): they show one or two images at a time, this shows up to 100 — and on
 * All sites they come from many different CDNs, so one bad CDN would turn 100 requests into
 * 200, half of them server-side fetches on our own backend. The `no-referrer` meta the app
 * sends already removes the reason that proxy existed (hotlink bans), and on the site that
 * prompted this the image is really gone (a browser User-Agent gets a 404), so the proxy
 * would fetch the same 404 twice.
 */
const NO_IMAGE_STORED = 'No image for this product'
const IMAGE_GONE = 'Image no longer available at the retailer'

export default function BrandThumbnail({ src, alt }) {
    return (
        <Box
            // The frame and the placeholder live here, so the cell keeps its size whether
            // the picture arrives, never existed, or 404s.
            title={src ? '' : NO_IMAGE_STORED}
            sx={{
                width: SIZE, height: SIZE, position: 'relative', borderRadius: 1,
                border: '1px solid', borderColor: 'divider', backgroundColor: 'grey.50',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
        >
            <IconSeeker icon="image" sx={{ color: 'grey.400', fontSize: '1.6rem' }} />
            {src && (
                <Box
                    component="img"
                    src={src}
                    alt={alt || ''}
                    // 100 rows means 100 external requests: only fetch what is scrolled into view.
                    loading="lazy"
                    onClick={() => { imageModalData.value = { fullSRC: src, title: alt || '' } }}
                    // Hide and move on, uncovering the icon underneath. Set on the DOM node
                    // rather than through state so there is no per-row re-render, and
                    // `visibility` rather than `display` so nothing reflows. The tooltip goes
                    // on the wrapper at the same time: until it fails, hovering says nothing.
                    onError={(e) => {
                        e.currentTarget.style.visibility = 'hidden'
                        e.currentTarget.parentElement.title = IMAGE_GONE
                    }}
                    sx={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%',
                        objectFit: 'contain', borderRadius: 1, backgroundColor: 'grey.50',
                        display: 'block', cursor: 'zoom-in',
                    }}
                />
            )}
        </Box>
    )
}

/**
 * Rows with `thumbnail_url` resolved: percent-decoded and forced to https (mixed content
 * would be blocked on our https app). Same transformation `useProxiedImage` does, applied
 * to the page in one pass instead of once per row.
 */
export function withThumbnails(rows, field) {
    return rows.map(row => {
        const raw = row[field]
        if (!raw) return { ...row, thumbnail_url: null }
        let url = raw
        if (/%[0-9A-Fa-f]{2}/.test(url)) {
            try {
                url = decodeURIComponent(url)
            } catch (e) {
                url = raw
            }
        }
        if (url.startsWith('http://')) {
            url = `https://${url.slice('http://'.length)}`
        }
        return { ...row, thumbnail_url: url }
    })
}
